import { action } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type { Course, CourseDateAnnotationSingle } from "@shared/models/course"
import type { CourseDateCombined } from "@shared/models/course-date-combined"
import { handleError } from "cc-frontend/error-handling"
import type { Args as EditAnnotationArgs } from "cc-frontend/components/planner/modals/edit-annotation"
import COURSE_DATE_ANNOTATION_HIDE from "cc-frontend/lib/actions/course/date/COURSE_DATE_ANNOTATION_HIDE"
import courseDateAnnotationUpdate from "cc-frontend/lib/actions/course/date/COURSE_DATE_ANNOTATION_UPDATE"
import type CourseCalendarEditor from "cc-frontend/services/course-calendar-editor"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type ModalManager from "cc-frontend/services/modal-manager"
import { cloneDeep } from "lodash-es"

interface Args {
  course: Course
  courseDate: CourseDateCombined
  annotation: CourseDateAnnotationSingle
  closeMenu: Function
}

export default class CourseDateAnnotationMenuComponent extends Component<Args> {
  @service dispatcher!: Dispatcher
  @service modalManager!: ModalManager
  @service courseCalendarEditor!: CourseCalendarEditor

  @action edit() {
    this.args.closeMenu()
    this.modalManager.openModal("planner/modals/edit-annotation", {
      allowRangeToggle: false,
      dateIs: "SINGLE_DATE",
      datesAreEditable: false,
      description: this.args.annotation.attributes.description,
      endDate: this.args.courseDate.dateString,
      isRange: false,
      minimumStartDate: this.args.courseDate.dateString,
      modalTitle: "Edit Special Event",
      startDate: this.args.courseDate.dateString,
      submitButtonText: "Update",
      title: this.args.annotation.attributes.title,
      submitAction: ({ title, description, endDate, startDate }) => {
        let newAnnotation = cloneDeep(this.args.annotation)
        newAnnotation.attributes.title = title
        newAnnotation.attributes.description = description
        this.dispatcher.dispatchPreparedAction(
          courseDateAnnotationUpdate({
            annotation: newAnnotation,
            course: this.args.course,
          })
        )
      },
    } as EditAnnotationArgs)
  }

  // TODO Change this to use the sub-menu like we do for all our other menus
  @action delete() {
    this.args.closeMenu()
    swal({
      title: "Turn Class Back On?",
      text: "This will also delete the note you left.",
      type: "question",
      showCancelButton: true,
    })
      .then(() => {
        this.courseCalendarEditor.toggleCustomDate(
          this.args.course,
          this.args.courseDate.dateString,
          true,
          false
        )
      })
      .then(() => {
        swal({
          type: "success",
          title: "The class is back on!",
        }).catch((error) => {
          if (error instanceof Error) handleError(error)
        })
      })
      .catch((error) => {
        if (error instanceof Error) handleError(error)
      })
  }

  @action hideClass() {
    this.args.closeMenu()
    swal({
      title: "Hide Class?",
      text: "Are you sure you want to hide the class for this day?",
      type: "warning",
      showCancelButton: true,
    })
      .then(() => {
        this.dispatcher.dispatchAction(COURSE_DATE_ANNOTATION_HIDE, {
          annotation: this.args.annotation,
          course: this.args.course,
        })
      })
      .then(() => {
        swal({
          type: "success",
          title: "The class is hidden!",
          text: 'Go to the "Day Menu" to turn the class back on.',
        }).catch((error) => {
          if (error instanceof Error) handleError(error)
        })
      })
      .catch((error) => {
        if (error instanceof Error) handleError(error)
      })
  }
}
